import { useEffect } from 'react';

// Motor de interação da Home: letras com física, partículas, combo,
// chacoalhar (celular e mouse), inclinação (giroscópio/mouse) e elasticidade no scroll.
// Tudo roda num único requestAnimationFrame que "dorme" quando nada se mexe.

const HUES = [185, 265, 320, 160];
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const rand = (a, b) => Math.random() * (b - a) + a;

export default function useNameFX({ rootRef, stageRef, bgRef, nameRef, fxRef, canvasRef }) {
  useEffect(() => {
    const root = rootRef.current;
    const stage = stageRef.current;
    const bg = bgRef.current;
    const nameEl = nameRef.current;
    const fx = fxRef.current;
    const canvas = canvasRef.current;
    if (!root || !stage || !bg || !nameEl || !fx || !canvas) return undefined;

    // Sem movimento: letras ficam estáticas
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = canvas.getContext('2d');
    const isTouch = window.matchMedia('(hover: none)').matches;

    const letters = [...nameEl.querySelectorAll('[data-letter]')].map((el) => ({
      el, x: 0, y: 0, vx: 0, vy: 0, r: 0, vr: 0, cx: 0, cy: 0, h: 60,
    }));

    const parts = [];
    const tilt = { x: 0, y: 0, tx: 0, ty: 0 };
    const ptr = { x: 0, y: 0, active: false };

    let W = 0;
    let H = 0;
    let raf = 0;
    let last = 0;
    let drewParts = false;
    let hasOrientation = false;
    let lastScroll = window.scrollY;
    const timers = new Set();
    const later = (fn, ms) => {
      const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
      timers.add(id);
      return id;
    };

    // ── Utilidades ────────────────────────────────────────────────────────────
    const vibrate = (p) => {
      try {
        if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return;
        navigator.vibrate?.(p);
      } catch { /* sem suporte */ }
    };

    const label = (text, x, y, cls = '') => {
      const el = document.createElement('span');
      el.className = `fx-label ${cls}`;
      el.textContent = text;
      el.style.left = `${clamp(x, 70, Math.max(70, W - 70))}px`;
      el.style.top = `${y}px`;
      fx.appendChild(el);
      el.addEventListener('animationend', () => el.remove(), { once: true });
    };

    const applyLetters = () => {
      letters.forEach((l) => {
        l.el.style.transform = (l.x || l.y || l.r)
          ? `translate3d(${l.x.toFixed(2)}px, ${l.y.toFixed(2)}px, 0) rotate(${l.r.toFixed(2)}deg)`
          : '';
      });
    };

    // ── Medidas (posição de repouso das letras, em coordenadas da seção) ─────
    const measure = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const sr0 = root.getBoundingClientRect();
      W = sr0.width;
      H = sr0.height;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const prevStage = stage.style.transform;
      stage.style.transform = '';
      letters.forEach((l) => { l.el.style.transform = ''; });

      const sr = root.getBoundingClientRect();
      const nr = nameEl.getBoundingClientRect();
      letters.forEach((l) => {
        const r = l.el.getBoundingClientRect();
        l.cx = r.left - sr.left + r.width / 2;
        l.cy = r.top - sr.top + r.height / 2;
        l.h = r.height;
        // offset horizontal da letra: mantém o gradiente contínuo no nome inteiro
        l.el.style.setProperty('--ox', `${(r.left - nr.left).toFixed(1)}px`);
      });

      stage.style.transform = prevStage;
      applyLetters();
    };

    // ── Partículas ────────────────────────────────────────────────────────────
    const burst = (x, y, n, power = 1) => {
      for (let i = 0; i < n; i += 1) {
        const a = rand(0, Math.PI * 2);
        const sp = rand(1.5, 7) * power;
        parts.push({
          x, y,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp - 1.5,
          life: 0,
          max: rand(40, 80),
          hue: HUES[(Math.random() * HUES.length) | 0],
          size: rand(1.5, 3.5),
        });
      }
      if (parts.length > 400) parts.splice(0, parts.length - 400);
      wake(); // eslint-disable-line no-use-before-define
    };

    // ── Impulsos nas letras ──────────────────────────────────────────────────
    const hit = (x, y) => {
      letters.forEach((l) => {
        const dx = l.cx + l.x - x;
        const dy = l.cy + l.y - y;
        const d = Math.hypot(dx, dy) || 1;
        const f = clamp(1 - d / (l.h * 3.2), 0.12, 1);
        l.vx += (dx / d) * 14 * f;
        l.vy += (dy / d) * 14 * f - 7 * f;
        l.vr += rand(-14, 14) * f;
      });
    };

    const explode = (text) => {
      letters.forEach((l) => {
        l.vx += rand(-26, 26);
        l.vy += rand(-34, -8);
        l.vr += rand(-40, 40);
      });
      const cy = letters[0] ? letters[0].cy : H / 2;
      burst(W / 2, cy, 140, 1.6);
      label(text, W / 2, cy - 70, 'hot');
      vibrate([30, 40, 30]);
    };

    // ── Loop ─────────────────────────────────────────────────────────────────
    const frame = (t) => {
      raf = 0;
      const dt = last ? Math.min(t - last, 40) : 16.7;
      last = t;
      const s = dt / 16.67;
      let active = false;

      // Inclinação suavizada
      tilt.x += (tilt.tx - tilt.x) * 0.1 * s;
      tilt.y += (tilt.ty - tilt.y) * 0.1 * s;
      if (Math.abs(tilt.tx - tilt.x) > 0.003 || Math.abs(tilt.ty - tilt.y) > 0.003) active = true;

      // Parallax do scroll
      const p = clamp(window.scrollY / (window.innerHeight || 1), 0, 1);
      const rx = -tilt.y * 5;
      const ry = tilt.x * 8;
      const flat = Math.abs(rx) < 0.02 && Math.abs(ry) < 0.02 && p < 0.002;
      stage.style.transform = flat
        ? ''
        : `perspective(900px) translate3d(0, ${(p * 70).toFixed(1)}px, 0) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale(${(1 - p * 0.1).toFixed(3)})`;
      stage.style.opacity = p > 0.002 ? String(1 - p * 0.85) : '';

      bg.style.setProperty('--tx', tilt.x.toFixed(3));
      bg.style.setProperty('--ty', tilt.y.toFixed(3));
      if (ptr.active) {
        bg.style.setProperty('--mx', `${ptr.x.toFixed(0)}px`);
        bg.style.setProperty('--my', `${ptr.y.toFixed(0)}px`);
      }

      // Física das letras
      const damp = 0.86 ** s;
      letters.forEach((l) => {
        if (ptr.active) {
          const dx = l.cx + l.x - ptr.x;
          const dy = l.cy + l.y - ptr.y;
          const d = Math.hypot(dx, dy);
          const R = l.h * 1.1;
          if (d < R && d > 0.01) {
            const f = 1 - d / R;
            const push = f * f * 2.4 * s;
            l.vx += (dx / d) * push;
            l.vy += (dy / d) * push;
            l.vr += (dx > 0 ? 1 : -1) * f * 0.8 * s;
          }
        }

        l.vx += -0.06 * l.x * s;
        l.vy += -0.06 * l.y * s;
        l.vr += -0.05 * l.r * s;
        l.vx *= damp;
        l.vy *= damp;
        l.vr *= damp;
        l.x = clamp(l.x + l.vx * s, -260, 260);
        l.y = clamp(l.y + l.vy * s, -260, 260);
        l.r += l.vr * s;

        if (
          Math.abs(l.x) > 0.05 || Math.abs(l.y) > 0.05 || Math.abs(l.r) > 0.1 ||
          Math.abs(l.vx) > 0.05 || Math.abs(l.vy) > 0.05 || Math.abs(l.vr) > 0.1
        ) active = true;
      });

      if (active) {
        applyLetters();
      } else {
        letters.forEach((l) => {
          l.x = 0; l.y = 0; l.vx = 0; l.vy = 0; l.r = 0; l.vr = 0;
        });
        applyLetters();
      }

      // Partículas
      if (parts.length || drewParts) {
        ctx.clearRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'lighter';
        let w = 0;
        for (let i = 0; i < parts.length; i += 1) {
          const q = parts[i];
          q.life += s;
          q.vy += 0.12 * s;
          q.vx *= 0.985 ** s;
          q.x += q.vx * s;
          q.y += q.vy * s;
          const k = 1 - q.life / q.max;
          if (k > 0) {
            ctx.fillStyle = `hsla(${q.hue}, 100%, 60%, ${k.toFixed(3)})`;
            ctx.beginPath();
            ctx.arc(q.x, q.y, q.size * k + 0.4, 0, 6.2832);
            ctx.fill();
            parts[w] = q;
            w += 1;
          }
        }
        parts.length = w;
        drewParts = w > 0;
      }
      if (parts.length) active = true;

      if (active) {
        raf = requestAnimationFrame(frame);
      } else {
        last = 0;
      }
    };

    function wake() {
      if (!raf) raf = requestAnimationFrame(frame);
    }

    // ── Eventos: toque / combo ───────────────────────────────────────────────
    let combo = 0;
    let comboTimer = 0;
    let taps = 0;
    let asked = false;

    const needsPermission =
      typeof DeviceMotionEvent !== 'undefined' &&
      typeof DeviceMotionEvent.requestPermission === 'function';

    const onTap = (e) => {
      const sr = root.getBoundingClientRect();
      const x = e.clientX - sr.left;
      const y = e.clientY - sr.top;

      taps += 1;
      combo = comboTimer ? combo + 1 : 1;
      clearTimeout(comboTimer);
      comboTimer = later(() => { combo = 0; comboTimer = 0; }, 700);

      hit(x, y);
      burst(x, y, 22);
      vibrate(12);

      if (combo >= 10) {
        explode('MAX COMBO!!');
        combo = 0;
      } else if (combo >= 2) {
        label(`COMBO x${combo}`, x, y - 30, combo >= 5 ? 'hot' : '');
      } else if (taps === 3 && isTouch) {
        label('tente chacoalhar o celular 📱', x, y - 40);
      }

      // iOS exige pedir permissão dentro de um gesto do usuário
      if (needsPermission && !asked) {
        asked = true;
        DeviceMotionEvent.requestPermission()
          .then((r) => {
            if (r === 'granted') {
              attachSensors(); // eslint-disable-line no-use-before-define
              label('movimento ativado 📳', W / 2, y - 60);
            }
          })
          .catch(() => {});
      }
      wake();
    };

    // ── Eventos: ponteiro (repelir letras + inclinar + chacoalhar o mouse) ───
    let lastX = 0;
    let lastDir = 0;
    let lastMouseShake = 0;
    let reversals = [];

    const mouseShakeCheck = (e) => {
      if (e.pointerType !== 'mouse') return;
      const dx = e.clientX - lastX;
      lastX = e.clientX;
      if (Math.abs(dx) < 14) return;
      const dir = dx > 0 ? 1 : -1;
      const now = performance.now();
      if (lastDir && dir !== lastDir) {
        reversals.push(now);
        reversals = reversals.filter((tm) => now - tm < 600);
        if (reversals.length >= 5 && now - lastMouseShake > 1500) {
          lastMouseShake = now;
          reversals = [];
          explode('CHACOALHOU!');
        }
      }
      lastDir = dir;
    };

    const onMove = (e) => {
      const sr = root.getBoundingClientRect();
      ptr.x = e.clientX - sr.left;
      ptr.y = e.clientY - sr.top;
      ptr.active = true;
      if (!hasOrientation && W && H) {
        tilt.tx = clamp((ptr.x / W - 0.5) * 2, -1, 1);
        tilt.ty = clamp((ptr.y / H - 0.5) * 2, -1, 1);
      }
      mouseShakeCheck(e);
      wake();
    };

    const onLeave = () => {
      ptr.active = false;
      if (!hasOrientation) {
        tilt.tx = 0;
        tilt.ty = 0;
      }
      wake();
    };

    // ── Sensores: chacoalhar e inclinar o celular ────────────────────────────
    let lm = null;
    let lastSpike = 0;
    let lastShake = 0;

    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const cur = { x: a.x || 0, y: a.y || 0, z: a.z || 0 };
      if (lm) {
        const d = Math.abs(cur.x - lm.x) + Math.abs(cur.y - lm.y) + Math.abs(cur.z - lm.z);
        if (d > 32) {
          const now = Date.now();
          // dois "solavancos" em menos de 700ms = chacoalhada
          if (now - lastSpike < 700 && now - lastShake > 1500) {
            lastShake = now;
            explode('CHACOALHOU! 📳');
          }
          lastSpike = now;
        }
      }
      lm = cur;
    };

    const onOrient = (e) => {
      if (e.gamma == null || e.beta == null) return;
      hasOrientation = true;
      const nx = clamp(e.gamma / 30, -1, 1);
      const ny = clamp((e.beta - 45) / 30, -1, 1);
      if (Math.abs(nx - tilt.tx) > 0.02 || Math.abs(ny - tilt.ty) > 0.02) {
        tilt.tx = nx;
        tilt.ty = ny;
        wake();
      }
    };

    let sensorsOn = false;
    function attachSensors() {
      if (sensorsOn) return;
      sensorsOn = true;
      window.addEventListener('devicemotion', onMotion);
      window.addEventListener('deviceorientation', onOrient);
    }
    if (!needsPermission && 'DeviceMotionEvent' in window) attachSensors();

    // ── Scroll: letras "arrastam" como elástico ──────────────────────────────
    const onScroll = () => {
      const y = window.scrollY;
      const dy = clamp(y - lastScroll, -60, 60);
      lastScroll = y;
      if (dy && y < window.innerHeight * 1.5) {
        letters.forEach((l, i) => {
          const k = 0.5 + i / letters.length;
          l.vy += dy * 0.07 * k;
          l.vr += dy * 0.03 * (i % 2 ? 1 : -1) * k;
        });
      }
      wake();
    };

    let resizeRaf = 0;
    const onResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => { measure(); wake(); });
    };

    // ── Montagem ─────────────────────────────────────────────────────────────
    measure();
    document.fonts?.ready.then(measure);
    later(measure, 1300); // depois da animação de entrada

    nameEl.addEventListener('click', onTap);
    root.addEventListener('pointermove', onMove, { passive: true });
    root.addEventListener('pointerleave', onLeave);
    root.addEventListener('pointerup', onLeave);
    root.addEventListener('pointercancel', onLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    wake();

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizeRaf);
      timers.forEach(clearTimeout);
      nameEl.removeEventListener('click', onTap);
      root.removeEventListener('pointermove', onMove);
      root.removeEventListener('pointerleave', onLeave);
      root.removeEventListener('pointerup', onLeave);
      root.removeEventListener('pointercancel', onLeave);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('devicemotion', onMotion);
      window.removeEventListener('deviceorientation', onOrient);
      fx.querySelectorAll('.fx-label').forEach((el) => el.remove());
      stage.style.transform = '';
      stage.style.opacity = '';
    };
  }, [rootRef, stageRef, bgRef, nameRef, fxRef, canvasRef]);
}
